import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useTokenLoginHandler } from "../hooks/useTokenLoginHandler";
import { Form, Input, Button, notification, Divider, Checkbox, Modal } from "antd";
import {
  sendOtp,
  verifyOtp,
  getGoogleAuthUrl,
  getMicrosoftAuthUrl,
  acceptTerms,
} from "../services/authAPI";
import { getSendOtpErrorDescription } from "../utils/otpErrorMessages";
import {
  resolvePostLoginPath,
  clearOAuthStoredRedirect,
  persistRedirectBeforeOAuth,
  readPendingOAuthTerms,
  clearPendingOAuthTerms,
} from "../utils/postLoginRedirect";
import { LoginOutlined, GoogleOutlined } from "@ant-design/icons";

const OtpLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const pendingOAuthForTerms =
    searchParams.get("oauth_consent") === "1" ? readPendingOAuthTerms() : null;
  const isOAuthTermsStep = Boolean(pendingOAuthForTerms);
  const [form] = Form.useForm();
  const [otpRequested, setOtpRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [processingToken, setProcessingToken] = useState(false);
  /** From send-otp / registration: user already accepted terms in DB */
  const [serverTermsAccepted, setServerTermsAccepted] = useState(false);
  /** Local checkbox on code screen (stays checked until user unchecks) */
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedOauthPendingTerms, setAgreedOauthPendingTerms] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const termlyScriptAdded = useRef(false);
  const oauthTermsNotifiedRef = useRef(false);

  const handleTokenLogin = useTokenLoginHandler(
    login,
    navigate,
    setLoading,
    setProcessingToken,
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(location.search);
    if (params.get("token")) {
      return;
    }
    if (params.get("oauth_consent") === "1") {
      return;
    }
    const next = resolvePostLoginPath(location);
    clearOAuthStoredRedirect();
    navigate(next || "/", { replace: true });
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    const emailFromRegistration = location.state?.email;
    if (emailFromRegistration) {
      setEmail(emailFromRegistration);
      setOtpRequested(true);
      form.setFieldsValue({ email: emailFromRegistration });
      setServerTermsAccepted(Boolean(location.state?.terms_accepted));
    }
  }, [location.state, form]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("oauth_consent") !== "1") return;
    if (!readPendingOAuthTerms()) {
      navigate(buildLoginRedirectUrl(), { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!isOAuthTermsStep) {
      oauthTermsNotifiedRef.current = false;
      return;
    }
    if (oauthTermsNotifiedRef.current) return;
    oauthTermsNotifiedRef.current = true;
    notification.warning({
      message: "Terms and Conditions",
      description:
        "Please read and check the box below to accept the Terms and Conditions and continue into the app.",
      duration: 8,
    });
  }, [isOAuthTermsStep]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const token = sp.get("token");
    const error = sp.get("error");
    if (error) {
      clearPendingOAuthTerms();
      notification.error({
        message: "Login Failed",
        description: error,
      });
      window.history.replaceState(
        {},
        document.title,
        buildLoginRedirectUrl()
      );
    } else if (token) {
      setProcessingToken(true);
      handleTokenLogin(token, location);
    }
  }, [location, handleTokenLogin]);

  const redirectToOAuthProvider = async (getAuthUrl, errorLabel) => {
    try {
      persistRedirectBeforeOAuth(location);
      window.location.href = await getAuthUrl();
    } catch (error) {
      clearPendingOAuthTerms();
      console.error(`${errorLabel} Login Error:`, error);
      notification.error({ message: `Failed to initiate ${errorLabel} Login` });
    }
  };

  const handleGoogleLogin = () =>
    redirectToOAuthProvider(getGoogleAuthUrl, "Google");

  const handleMicrosoftLogin = () =>
    redirectToOAuthProvider(getMicrosoftAuthUrl, "Microsoft");

  const handleSendOtp = async (values) => {
    setLoading(true);
    try {
      const response = await sendOtp(values.email);
      setEmail(values.email);
      setOtpRequested(true);
      setServerTermsAccepted(Boolean(response.data?.terms_accepted));
      notification.success({ message: "Login code sent to your email" });
    } catch (error) {
      notification.error({
        message: "Failed to send login code",
        description: getSendOtpErrorDescription(error, values.email),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const response = await sendOtp(email);
      setServerTermsAccepted(Boolean(response.data?.terms_accepted));
      notification.success({ message: "New login code sent to your email" });
    } catch (error) {
      notification.error({
        message: "Failed to resend login code",
        description: getSendOtpErrorDescription(error, email),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (values) => {
    if (!serverTermsAccepted && !agreedToTerms) {
      notification.warning({
        message: "Terms acceptance required",
        description: "You must agree to the Terms and Conditions to continue.",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await verifyOtp(email, values.code);
      let { token, user } = response.data;

      if (!serverTermsAccepted && agreedToTerms) {
        try {
          const termsRes = await acceptTerms(email, token);
          user = {
            ...user,
            terms_accepted_at: termsRes.data?.terms_accepted_at,
          };
        } catch (err) {
          notification.error({
            message: "Failed to record terms acceptance",
            description: err.response?.data?.detail || "Please try again.",
          });
          return;
        }
      }

      login(user, token);
      localStorage.removeItem("login_type");
      const next = resolvePostLoginPath(location);
      clearOAuthStoredRedirect();
      navigate(next || "/submittals", { replace: true });
    } catch (error) {
      notification.error({
        message: "Invalid or expired code",
        description:
          error.response?.data?.detail?.message || "Please try again",
      });
    } finally {
      setLoading(false);
    }
  };

  const openTermsModal = () => {
    setTermsModalOpen(true);
    // inject Termly script once
    if (
      !termlyScriptAdded.current &&
      !document.getElementById("termly-jssdk")
    ) {
      const d = document;
      const s = "script";
      const id = "termly-jssdk";
      const js = d.createElement(s);
      js.id = id;
      js.src = "https://app.termly.io/embed-policy.min.js";
      const tjs = d.getElementsByTagName(s)[0];
      tjs.parentNode.insertBefore(js, tjs);
      termlyScriptAdded.current = true;
    }
  };

  const handleTermsModalCancel = () => {
    setTermsModalOpen(false);
  };

  const buildLoginRedirectUrl = () => {
    const next = resolvePostLoginPath(location);
    return next ? `/login?redirect=${encodeURIComponent(next)}` : "/login";
  };

  const completeOAuthWithTerms = async () => {
    if (!agreedOauthPendingTerms) {
      notification.warning({
        message: "Accept Terms and Conditions",
        description: "Check the box to agree to the Terms and Conditions, then continue.",
      });
      return;
    }
    const pending = readPendingOAuthTerms();
    if (!pending) {
      navigate(buildLoginRedirectUrl(), { replace: true });
      return;
    }
    setLoading(true);
    try {
      const termsRes = await acceptTerms(pending.user.email, pending.token);
      const user = {
        ...pending.user,
        terms_accepted_at:
          termsRes.data?.terms_accepted_at || new Date().toISOString(),
      };
      login(user, pending.token);
      clearPendingOAuthTerms();
      localStorage.removeItem("login_type");
      const next = resolvePostLoginPath(location);
      clearOAuthStoredRedirect();
      navigate(next || "/submittals", { replace: true });
    } catch (err) {
      notification.error({
        message: "Failed to record terms acceptance",
        description: err.response?.data?.detail || "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelOAuthTerms = () => {
    clearPendingOAuthTerms();
    navigate(buildLoginRedirectUrl(), { replace: true });
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="flex bg-white rounded-lg shadow-md w-full max-w-4xl overflow-hidden">
        {/* Image Section - Takes Full Space */}
        <div className="w-1/2 hidden md:block">
          <img
            src="/login.webp"
            alt="Login"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Form Section - Vertically Centered */}
        <div className="w-full md:w-1/2 flex flex-col justify-center p-8">
          {!isOAuthTermsStep && (
            <>
              <h1 className="text-2xl font-bold text-left text-gray-900 mb-2">
                Welcome Back
              </h1>
              <p className="text-left text-gray-600 mb-6">
                Login to access your dashboard
              </p>
            </>
          )}

          {isOAuthTermsStep && pendingOAuthForTerms ? (
            <>
              <h1 className="text-2xl font-bold text-left text-gray-900 mb-2">
                Welcome Back
              </h1>
              <p className="text-left text-gray-600 mb-6">
                Login to access your dashboard
              </p>
              <div className="w-full text-left max-w-sm">
                <div className="mb-4 text-gray-700">
                  <p>Signed in with Google or Microsoft:</p>
                  <p className="font-medium text-gray-900">
                    {pendingOAuthForTerms.user.email}
                  </p>
                </div>
                <p className="text-sm text-amber-800 mb-3">
                  Please read and accept the Terms and Conditions to continue
                </p>
                <div className="flex items-center gap-2 mb-6">
                  <Checkbox
                    id="terms-checkbox-oauth-pending"
                    checked={agreedOauthPendingTerms}
                    onChange={(e) =>
                      setAgreedOauthPendingTerms(e.target.checked)
                    }
                  />
                  <span className="text-sm text-gray-600">
                    I agree to the{" "}
                    <button
                      type="button"
                      className="text-blue-600 hover:underline font-medium bg-transparent border-none p-0 cursor-pointer"
                      onClick={openTermsModal}
                    >
                      Terms and Conditions
                    </button>
                  </span>
                </div>
                <div className="flex flex-col gap-2 max-w-sm">
                  <Button
                    type="primary"
                    disabled={!agreedOauthPendingTerms}
                    loading={loading}
                    onClick={completeOAuthWithTerms}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg py-2"
                  >
                    <LoginOutlined />
                    Continue
                  </Button>
                  <Button
                    type="link"
                    onClick={cancelOAuthTerms}
                    className="text-gray-500 w-full text-left px-0"
                  >
                    Use a different way to sign in
                  </Button>
                </div>
              </div>
            </>
          ) : processingToken ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600 font-medium">Logging you in...</p>
            </div>
          ) : (
            <Form
              form={form}
              onFinish={otpRequested ? handleVerifyOtp : handleSendOtp}
              layout="vertical"
              requiredMark={false}
            >
              {!otpRequested ? (
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: "Please input your email!" },
                    { type: "email", message: "Please enter a valid email!" },
                  ]}
                >
                  <Input
                    placeholder="Enter your work email"
                    className="rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
                  />
                </Form.Item>
              ) : (
                <>
                  <div className="mb-4 text-gray-700 text-left">
                    <p>Login code sent to:</p>
                    <p className="font-medium text-gray-900">{email}</p>
                  </div>

                  <Form.Item
                    label="Enter login code"
                    name="code"
                    rules={[
                      { required: true, message: "Please input the code!" },
                    ]}
                    extra="Your login code expires in 5 minutes"
                    className="mb-2"
                  >
                    <Input
                      placeholder="Enter the 6-digit code"
                      maxLength={6}
                      className="rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
                    />
                  </Form.Item>
                  {!serverTermsAccepted && (
                    <Form.Item className="mb-4 mt-0">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="terms-checkbox"
                          checked={agreedToTerms}
                          onChange={(e) => setAgreedToTerms(e.target.checked)}
                        />
                        <span className="text-sm text-gray-600">
                          I agree to the{" "}
                          <button
                            type="button"
                            className="text-blue-600 hover:underline font-medium bg-transparent border-none p-0 cursor-pointer"
                            onClick={openTermsModal}
                          >
                            Terms and Conditions
                          </button>
                        </span>
                      </div>
                    </Form.Item>
                  )}
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg py-2"
                    >
                      <LoginOutlined /> Verify Code
                    </Button>
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="default"
                      onClick={handleResendOtp}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-blue-600 border-blue-600 hover:border-blue-700 font-semibold rounded-lg py-2 border-2"
                    >
                      Resend Code
                    </Button>
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="link"
                      onClick={() => {
                        setOtpRequested(false);
                        setServerTermsAccepted(false);
                        setAgreedToTerms(false);
                      }}
                      className="w-full text-gray-500 hover:text-gray-700"
                    >
                      Use different email
                    </Button>
                  </Form.Item>

                  {/* Spam Folder Warning Note */}
                  <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg mt-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-amber-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-semibold text-amber-800 mb-1">
                          Important
                        </h4>
                        <p className="text-sm text-amber-700 leading-relaxed">
                          Check your spam folder and mark our emails as "Not
                          Spam" so you can receive submittal/RFI emails to your
                          inbox going forward.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {!otpRequested && (
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg py-3"
                  >
                    <LoginOutlined /> Continue
                  </Button>
                </Form.Item>
              )}

              {!otpRequested && (
                <>
                  <Divider plain>Or</Divider>
                  <div className="flex flex-col gap-2">
                    <Button
                      icon={<GoogleOutlined style={{ color: "#DB4437" }} />}
                      onClick={handleGoogleLogin}
                      className="w-full flex items-center justify-center gap-2 font-semibold rounded-lg py-3"
                    >
                      Sign in with Google
                    </Button>
                    <Button
                      icon={
                        <img
                          src="https://learn.microsoft.com/en-us/entra/identity-platform/media/howto-add-branding-in-apps/ms-symbollockup_mssymbol_19.svg"
                          alt=""
                          className="h-4 w-4"
                        />
                      }
                      onClick={handleMicrosoftLogin}
                      className="w-full flex items-center justify-center gap-2 font-semibold rounded-lg py-3"
                    >
                      Sign in with Microsoft
                    </Button>
                  </div>
                </>
              )}
            </Form>
          )}

          {!otpRequested && !processingToken && !isOAuthTermsStep && (
            <div className="text-left mt-4 text-gray-600">
              <Link to="/register" className="text-blue-600 hover:underline">
                Not registered yet? Sign up here
              </Link>
            </div>
          )}
          {/* New Static Outgoing Links */}
          <div className="text-left mt-4 text-gray-600 text-sm">
            <p>
              <a
                href="https://www.submittallink.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Learn More
              </a>{" "}
            </p>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      <Modal
        open={termsModalOpen}
        onCancel={handleTermsModalCancel}
        title="Terms and Conditions"
        width={800}
        footer={
          <Button type="primary" onClick={handleTermsModalCancel}>
            Close
          </Button>
        }
        destroyOnClose={false}
      >
        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <div
            name="termly-embed"
            data-id="0bcb9f42-42db-4c23-b766-36ee44afcc87"
          ></div>
        </div>
      </Modal>
    </div>
  );
};

export default OtpLogin;
